import './table.scss';

import React, { useEffect, useRef } from 'react';

import { refetchCampaigns, useCampaigns } from '../../lib/data-hooks';

import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { Button } from '@mui/material';

import Configuration from '../../configuration';
import APIService, { CampaignStatus } from '../../shared/api-service';
import ICampaign from '../../shared/ICampaign';
import EnhancedTable from './EnhancedTable';

export const Table: React.FC<{}> = () => {
  const accessToken = APIService.getAccessToken(true);
  const { data: dataPendingCampaigns, isLoading: isLoadingPendingCampaigns, isError: isErrorPendingCampaigns }: { data: Array<ICampaign>, isLoading: boolean, isError: any } = useCampaigns(CampaignStatus.Pending, accessToken);
  const { data: dataScheduledCampaigns, isLoading: isLoadingScheduledCampaigns, isError: isErrorScheduledCampaigns }: { data: Array<ICampaign>, isLoading: boolean, isError: any } = useCampaigns(CampaignStatus.Scheduled, accessToken);
  const { data: dataSubmittedCampaigns, isLoading: isLoadingSubmittedCampaigns, isError: isErrorSubmittedCampaigns }: { data: Array<ICampaign>, isLoading: boolean, isError: any } = useCampaigns(CampaignStatus.Submitted, accessToken);

  const refIntervalScheduledTimer: any = useRef();
  const refIntervalSubmittedTimer: any = useRef();

  const getCampaignToBeSentFirst = (campaigns: Array<ICampaign>): ICampaign => {
    const arrayClone = [...campaigns];
    let sortedCampaigns = arrayClone.sort((a: any, b: any) => {
      const aDate = new Date(a.scheduledDateTime);
      const bDate = new Date(b.scheduledDateTime);
      if (aDate < bDate) {
        return -1
      } else if (aDate > bDate) {
        return +1
      } else {
        return 0;
      }
    })
    return sortedCampaigns[0];
  }

  const getMSecBetweenDateAndNow = (date: string) => {
    const dateTime: number = (new Date(date)).valueOf();
    const now: number = Date.now().valueOf();
    const deltaTimeMSec: number = Math.max(0, dateTime - now);
    return deltaTimeMSec;
  }

  useEffect(() => {
    if (dataSubmittedCampaigns) {
      if (refIntervalSubmittedTimer.current) {
        const campaignToBeSentOutSoonest = refIntervalSubmittedTimer.current.campaign;
        const indexCampaign = dataSubmittedCampaigns.findIndex((campaign) => campaign.id === campaignToBeSentOutSoonest.id);
        if (indexCampaign >= 0) {
          clearInterval(refIntervalSubmittedTimer.current.intervalTimer);
          refIntervalSubmittedTimer.current = undefined;
        }
      }
    }
  }, [dataSubmittedCampaigns])

  useEffect(() => {
    let timer: any = undefined;

    // If we have an interval timer running on scheduled campaigns and the scheduled campaign is no longer found within the dataset
    // then stop the scheduled campaign interval timer and start a submitted campaign interval timer.Inger
    const thereIsAtLeastOneScheduledCampaign: boolean = dataScheduledCampaigns && dataScheduledCampaigns.length > 0;
    const campaignToBeSentOutSoonest = thereIsAtLeastOneScheduledCampaign ? getCampaignToBeSentFirst(dataScheduledCampaigns) : undefined;
    let dataSetIncludesCampaignToBeSentOutSoonest = false;
    if (refIntervalScheduledTimer.current) {
      if (campaignToBeSentOutSoonest) {
        const indexCampaign = dataScheduledCampaigns.findIndex((campaign) => campaign.id === campaignToBeSentOutSoonest.id);
        dataSetIncludesCampaignToBeSentOutSoonest = indexCampaign >= 0;
      }

      if (!dataScheduledCampaigns || !dataSetIncludesCampaignToBeSentOutSoonest || dataScheduledCampaigns.length === 0) {
        // Get a copy of the campaign from the scheduled interval timer, and then stop the timer and clear out the reference.
        const campaign = refIntervalScheduledTimer.current.campaign;
        clearInterval(refIntervalScheduledTimer.current.intervalTimer);
        refIntervalScheduledTimer.current = undefined;

        // Clear any previous running submitted campaign interval timer.
        if (refIntervalSubmittedTimer.current) {
          clearInterval(refIntervalSubmittedTimer.current.intervalTimer);
        }

        // A campaign has been sent - start polling the submitted campaigns until the one which was sent shows up in that list.
        reloadData('submitted');

        // Establish a new submitted campaign interval timer.
        refIntervalSubmittedTimer.current = {
          campaign: campaign,
          intervalTimer: setInterval(() => {
            reloadData('submitted');
          }, 5000)
        }
      }
    }

    // if we have at least one scheduled campaign then establish a timer for when to start checking to see if the scheduled campaign
    // gets removed from the scheduled data
    if (dataScheduledCampaigns && dataScheduledCampaigns.length > 0) {
      const campaignToBeSentOutSoonest = getCampaignToBeSentFirst(dataScheduledCampaigns);
      const deltaTimeMSec = getMSecBetweenDateAndNow(campaignToBeSentOutSoonest.scheduledDateTime);
      console.log(`Setting timer for ${deltaTimeMSec / 1000} + 5 seconds`)

      timer = setTimeout(() => {
        if (refIntervalScheduledTimer.current) {
          clearInterval(refIntervalScheduledTimer.current.intervalTimer)
        }

        refIntervalScheduledTimer.current = {
          campaign: campaignToBeSentOutSoonest,
          intervalTimer: setInterval(() => {
            reloadData('scheduled');
          }, 5000)
        }
      }, deltaTimeMSec + 5000)
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
        timer = undefined;
      }
    }
  }, [dataScheduledCampaigns])

  const reloadData = (tableName: string | undefined = undefined) => {
    if (!tableName) {
      refetchCampaigns(CampaignStatus.Pending);
      refetchCampaigns(CampaignStatus.Scheduled);
      refetchCampaigns(CampaignStatus.Submitted);
    } else if (tableName.toLowerCase() === "pending") {
      refetchCampaigns(CampaignStatus.Pending);
    } else if (tableName.toLowerCase() === "scheduled") {
      refetchCampaigns(CampaignStatus.Scheduled);
    } else if (tableName.toLowerCase() === "submitted") {
      refetchCampaigns(CampaignStatus.Submitted);
    }
  }

  const onLogout = () => {
    // Remove the jsforce access token from local storage.  This
    // Will cause the login screen to be displayed when the app is reloaded.
    Configuration.removeAccessToken();

    // Use the jsforce connection from index.html to logout - to prevent
    // reuse of the access token.
    const wind: any = window;
    let connection = wind?.onecontrol?.connection;
    if (connection) {
      connection.logout(function (error: any) {
        console.log(error);
      })
      delete wind.onecontrol;
    }

    // Reload the app.
    window.location.href = Configuration.getRedirectURI();
  }

  return (
    <div>
      <span className="title">Marketing Campaign Editor</span><span className="rhs"><Button onClick={onLogout}>Log Off</Button></span><br /><br />

      <EnhancedTable type={'pending'} isLoading={isLoadingPendingCampaigns} rows={dataPendingCampaigns ? dataPendingCampaigns : []} reloadData={reloadData} title={'Pending Campaigns'}
        readOnly={false} deletable={true} cancelable={false} sortModel={[{ field: 'createdDateTime', sort: 'desc' }]} hideFields={['scheduledDateTime', 'submittedDateTime']} />

      <EnhancedTable type={'scheduled'} isLoading={isLoadingScheduledCampaigns} rows={dataScheduledCampaigns ? dataScheduledCampaigns : []} reloadData={reloadData} title={'Scheduled Campaigns'}
        titleIcon={<CalendarMonthIcon className={'title-icon'} />} readOnly={true} deletable={true} cancelable={true} sortModel={[{ field: 'scheduledDateTime', sort: 'asc' }]} hideFields={['submittedDateTime']} />

      <EnhancedTable type={'submitted'} isLoading={isLoadingSubmittedCampaigns} rows={dataSubmittedCampaigns ? dataSubmittedCampaigns : []} reloadData={reloadData} title={'Submitted Campaigns'}
        readOnly={true} deletable={false} cancelable={false} sortModel={[{ field: 'submittedDateTime', sort: 'desc' }]} />

      {(isErrorPendingCampaigns || isErrorScheduledCampaigns || isErrorSubmittedCampaigns) &&
        <div className="error-text">
          Please log off and then log in again.
          <br /><br />
          {isErrorPendingCampaigns ? "Pending Campaigns:\n" + isErrorPendingCampaigns : ""}
          <br />< br />
          {isErrorScheduledCampaigns ? "Scheduled Campaigns:\n" + isErrorScheduledCampaigns : ""}
          <br />< br />
          {isErrorSubmittedCampaigns ? "Submitted Campaigns:\n" + isErrorSubmittedCampaigns : ""}
        </div>
      }
    </div>
  );
};
